/**
 * Блок «Цены, труд и доходы населения» (US-35.1 FR-5, FR-6) — группы
 * выдачи `INFLATION` и `LABOR_INCOME`, каждая своими карточками.
 *
 * **Инфляция** (FR-5): ИПЦ и три его составляющие — продовольственные,
 * непродовольственные товары и платные услуги. База индекса выводится из
 * ответа и подписывается явно: сегодня поставлена база «к соответствующему
 * периоду прошлого года», а не «к декабрю предыдущего года», и подменять
 * подпись запрещено (FR-5.2). ИПЦ публикуется только по области — на
 * районе карточка подписывается уровнем (FR-5.3, FR-9.2).
 *
 * **Рынок труда** (FR-6): уровень безработицы, численность безработных и
 * рост заработной платы; у роста подписываются вид индекса и база
 * сравнения — они приходят из ответа, а не подставляются экраном (FR-6.2).
 * Среднемесячная зарплата в тенге и среднедушевые доходы в этой волне не
 * выводятся — не нулём и не прочерком, а отсутствием строки (FR-6.3):
 * выдача их не возвращает.
 *
 * Порядок групп и показателей внутри — из поля `order` ответа.
 */
import { useTranslation } from 'react-i18next';

import { EconomyIndicatorCard } from './EconomyIndicatorCard';
import { BlockLoader } from '../../../components/common/BlockLoader';
import type { PricesLaborResponse } from '../../../types/analytics';

interface PricesLaborBlockProps {
  data: PricesLaborResponse | null;
  loading: boolean;
}

export const PricesLaborBlock = ({ data, loading }: PricesLaborBlockProps) => {
  const { t } = useTranslation();

  if (data !== null && (!data.widget_enabled || !data.level_supported)) {
    return null;
  }

  const indicators = (data?.groups ?? []).flatMap((group) => group.indicators);

  return (
    <section className="space-y-3.5" data-testid="block-economy-prices-labor">
      <div className="flex items-center gap-4 pt-2">
        <h3 className="shrink-0 text-lg font-extrabold">
          {t('analytics.economy.blocks.pricesLaborTitle')}
        </h3>
        <div className="h-px flex-1 bg-surface-border" aria-hidden="true" />
      </div>

      {loading ? (
        <BlockLoader testId="economy-prices-labor-loader" />
      ) : indicators.length === 0 ? (
        <p
          className="text-sm lowercase text-text-muted"
          data-testid="block-placeholder"
        >
          {t('common.noData')}
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-3.5 xl:grid-cols-2">
          {indicators.map((indicator) => (
            <EconomyIndicatorCard
              key={indicator.key}
              indicator={indicator}
              testId={`economy-prices-labor-${indicator.key}`}
            />
          ))}
        </div>
      )}
    </section>
  );
};
